import { BridgeComponent } from "@hotwired/hotwire-native-bridge"
import { normalizePreference } from "../../audio/preferences.js"

const documentIds = new WeakMap()

// No engine here: unsupported APKs/browsers do not load this bridge component.
export default class extends BridgeComponent {
  static component = "audio"

  connect() {
    this.cleanup()
    this.removePendingCallbacks()
    this.pendingMessageCallbacks = []
    super.connect()
    if (!documentIds.has(document)) documentIds.set(document, crypto.randomUUID())
    this.clientId = documentIds.get(document)
    this.live = true
    this.sequence = 0
    this.settingsStatus = "Connecting audio…"
    this.renderSettings()
    this.touch = event => this.gesture(event)
    this.element.addEventListener("pointerdown", this.touch, { passive: true })
    this.beforeRequest = event => this.captureInteraction(event)
    this.beforeResponse = event => this.consumeOutcome(event)
    this.discardInteractions = () => { this.interactions = new WeakMap() }
    document.addEventListener("turbo:before-fetch-request", this.beforeRequest, true)
    document.addEventListener("turbo:before-fetch-response", this.beforeResponse, true)
    for (const name of ["turbo:visit", "turbo:before-cache", "visibilitychange"]) {
      document.addEventListener(name, this.discardInteractions)
    }
    this.send("connect", { clientId: this.clientId }, message => this.control(message.data))
  }

  disconnect() {
    this.send("disconnect", { clientId: this.clientId })
    this.cleanup()
    super.disconnect()
  }

  cleanup() {
    this.live = false
    this.pendingGesture = false
    this.element.removeEventListener("pointerdown", this.touch)
    document.removeEventListener("turbo:before-fetch-request", this.beforeRequest, true)
    document.removeEventListener("turbo:before-fetch-response", this.beforeResponse, true)
    for (const name of ["turbo:visit", "turbo:before-cache", "visibilitychange"]) {
      document.removeEventListener(name, this.discardInteractions)
    }
    this.closeChannel()
  }

  closeChannel() {
    this.interactions = new WeakMap()
    clearTimeout(this.deadline)
    clearTimeout(this.stateDeadline)
    this.deadline = null
    this.stateDeadline = null
    this.stateSequence = null
    this.pendingPreferences = null
    this.preferenceSnapshot = null
    this.settingsAvailable = false
    this.settingsStatus = "Audio unavailable in this app."
    if (this.channel) this.channel.close()
    this.channel = null
    this.identity = null
    this.granted = false
    this.renderSettings()
  }

  control(data) {
    if (!this.live || !this.element.isConnected) return
    if (["revoke", "unavailable"].includes(data.action)) {
      this.pendingGesture = false
      this.closeChannel()
      return
    }
    if (!this.validIdentity(data)) return
    if (data.action === "bootstrap") {
      this.closeChannel()
      this.settingsStatus = "Connecting audio…"
      this.renderSettings()
      this.identity = this.pickIdentity(data)
      this.sequence = 0
      try {
        this.channel = new BroadcastChannel(`vending-audio-v1:${data.activityEpoch}`)
        this.channel.onmessage = event => this.receive(event.data)
        this.channel.onmessageerror = () => this.fail()
        this.deadline = setTimeout(() => this.fail(), 3000)
        this.helloSequence = this.emit("hello", {})
      } catch { this.fail() }
    } else if (data.action === "grant" && this.matches(data)) {
      clearTimeout(this.deadline)
      this.deadline = null
      this.granted = true
      this.send("granted", this.pickIdentity(this.identity))
      this.emitContext()
      if (this.pendingGesture) {
        this.pendingGesture = false
        if (!document.hidden) this.send("gesture", this.pickIdentity(this.identity))
      }
    }
  }

  receive(data) {
    if (!this.matches(data) || data.type !== "ack") return
    if (data.sequence === this.helloSequence) {
      if (data.payload?.status !== "accepted") { this.fail(); return }
      this.helloSequence = null
      this.send("ready", this.pickIdentity(this.identity))
    } else if (this.granted && data.sequence === this.stateSequence) {
      const snapshot = normalizePreference(data.payload?.preferences)
      if (data.payload?.status !== "accepted") { this.fail(); return }
      clearTimeout(this.stateDeadline)
      this.stateDeadline = null
      this.stateSequence = null
      // A host already alive during a web deployment may predate preference snapshots.
      // Keep its accepted playback intact, but do not offer nonfunctional controls.
      if (!snapshot) {
        this.settingsAvailable = false
        this.settingsStatus = "Audio controls unavailable. Restart the app."
        this.renderSettings()
        return
      }
      this.settingsAvailable = true
      this.settingsStatus = data.payload.storageAvailable === true ? "" : "Changes won't be saved after closing the app."
      this.preferenceSnapshot = this.pendingPreferences || snapshot
      this.renderSettings()
      if (this.pendingPreferences) this.sendPreferences()
    }
  }

  emitContext() {
    if (!this.granted) return
    try {
      const payload = JSON.parse(this.element.dataset.audioContext || "null")
      // Modal documents without metadata inherit the current ambience.
      this.sendState("context", payload || { inherit: true })
    } catch { this.fail() }
  }

  gesture(event) {
    if (!this.live || !event.isTrusted || document.hidden || !this.element.isConnected ||
        event.target.closest("[disabled], [aria-disabled='true'], [inert]")) return
    if (!this.granted) { this.pendingGesture = true; return }
    this.send("gesture", this.pickIdentity(this.identity))
  }

  captureInteraction(event) {
    const form = event.target
    const options = event.detail?.fetchOptions
    if (!this.live || !this.granted || document.hidden || !this.element.isConnected ||
        form?.tagName !== "FORM" || !form.isConnected || !this.element.contains(form) ||
        !options || !["POST", "PUT", "PATCH", "DELETE"].includes(options.method?.toUpperCase())) return
    try {
      if (new URL(event.detail.url, window.location.href).origin !== new URL(window.location.href).origin) return
      const interaction = crypto.randomUUID()
      options.headers = new Headers(options.headers)
      options.headers.set("X-Vending-Audio-Interaction", interaction)
      this.interactions.set(form, { interaction, at: Date.now(), identity: this.pickIdentity(this.identity) })
    } catch { /* Audio never prevents a gameplay submission. */ }
  }

  consumeOutcome(event) {
    const pending = this.interactions.get(event.target)
    if (!pending || !this.live || !this.granted || !this.matches(pending.identity) ||
        document.hidden || !this.element.isConnected || !event.target.isConnected) return
    // Capture phase: read before Turbo replaces the form/body. Nothing is put in
    // DOM snapshots, and a later owner cannot relabel this request as its own.
    try {
      const response = event.detail?.fetchResponse?.response
      if (!response?.ok || new URL(response.url).origin !== new URL(window.location.href).origin) return
      const header = response.headers.get("X-Vending-Audio-Cue")
      if (!header || header.length > 512) return
      const outcome = JSON.parse(header)
      if (outcome?.interaction !== pending.interaction || !/^[a-z][a-z0-9_]{0,63}$/.test(outcome.key) ||
          typeof outcome.eventId !== "string" || !outcome.eventId.length || outcome.eventId.length > 128) return
      this.interactions.delete(event.target)
      this.cue(outcome.key, outcome.eventId, pending.at)
    } catch { /* Malformed/unavailable audio metadata is silent, not a UI error. */ }
  }

  // Also used by the bounded debug harness; no cue is queued for another owner.
  cue(key, eventId, at) {
    if (!this.granted || document.hidden || !this.element.isConnected) return
    if (!Number.isFinite(at) || Date.now() - at < 0 || Date.now() - at > 2000) return
    this.emit("cue", { key, at }, eventId)
  }

  preferences(payload) {
    const snapshot = normalizePreference(payload)
    if (!this.granted || !this.settingsAvailable || !snapshot || document.hidden) return
    this.preferenceSnapshot = snapshot
    this.pendingPreferences = snapshot
    this.renderSettings()
    if (this.stateSequence == null) this.sendPreferences()
  }

  sendPreferences() {
    const snapshot = this.pendingPreferences
    this.pendingPreferences = null
    this.sendState("preferences", snapshot)
  }

  sendState(type, payload) {
    this.stateSequence = this.emit(type, payload)
    clearTimeout(this.stateDeadline)
    this.stateDeadline = setTimeout(() => this.fail(), 3000)
  }

  changePreference(event) {
    if (!this.settingsAvailable || !this.preferenceSnapshot) return
    const key = event.currentTarget.dataset.preference
    if (!["music", "ambience", "sfx"].includes(key)) return
    const snapshot = this.preferenceSnapshot
    // Honor a previously saved master-off state without silently enabling other buses.
    const levels = snapshot.enabled ? snapshot : { ...snapshot, music: 0, ambience: 0, sfx: 0 }
    this.preferences({ ...levels, enabled: true, [key]: levels[key] > 0 ? 0 : 1 })
  }

  renderSettings() {
    const controls = this.element.querySelector("[data-audio-settings]")
    if (!controls) return
    controls.disabled = !this.settingsAvailable
    const status = this.element.querySelector("[data-audio-status]")
    status.textContent = this.settingsStatus
    status.hidden = !this.settingsStatus
    const snapshot = this.preferenceSnapshot
    for (const bus of ["music", "ambience", "sfx"]) {
      const toggle = controls.querySelector(`[data-preference="${bus}"]`)
      toggle?.setAttribute("aria-checked", String(snapshot?.enabled === true && snapshot[bus] > 0))
    }
  }

  emit(type, payload, eventId) {
    if (!this.channel || !this.identity) return null
    const sequence = ++this.sequence
    this.channel.postMessage({ ...this.identity, sequence, type, payload, ...(eventId ? { eventId } : {}) })
    return sequence
  }

  pickIdentity(data) {
    return { protocol: 1, activityEpoch: data.activityEpoch, hostEpoch: data.hostEpoch,
      generation: data.generation, clientId: this.clientId }
  }

  validIdentity(data) {
    return data.protocol === 1 && data.clientId === this.clientId &&
      [data.activityEpoch, data.hostEpoch].every(value => typeof value === "string" && /^[A-Za-z0-9-]{1,80}$/.test(value)) &&
      Number.isSafeInteger(data.generation) && data.generation >= 0
  }

  matches(data) {
    return this.identity && this.validIdentity(data) &&
      ["activityEpoch", "hostEpoch", "generation"].every(key => data[key] === this.identity[key])
  }

  fail() {
    this.pendingGesture = false
    if (this.identity) this.send("failed", this.pickIdentity(this.identity))
    this.closeChannel()
  }
}
